//Libraries
import { ComponentType, lazy, useEffect, useRef } from 'react';
import { Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { history } from 'utils/history';

// Components
// import Header from './shared/Header';
import Sidebar from 'components/@efz/Sidebar';
import Loading from 'components/Loading';
import RedefinePasswordDialog from 'components/RedefinePasswordDialog';

import { BASENAME_URL_LOGIN } from 'constants/settings';
//Constants
import { SESSION_EXPIRED } from 'constants/statusCodeHttp';
import { fonts } from 'constants/styles';

//Services
import lazyLoader from 'services/lazyLoader';
import { isFieldDefined } from 'services/util/auxiliaryUtils';

//Stores
import { useSidebarStore } from 'stores/sidebar';
import { useUserStore } from 'stores/user';

interface StyledAppMainContentProps {
    $isSidebarOpen?: boolean;
}

const StyledAppMainContent = styled.div.attrs<StyledAppMainContentProps>((props) => ({
    className: 'StyledAppMainContent',
    $isSidebarOpen: props?.$isSidebarOpen ?? true,
}))<StyledAppMainContentProps>`
    background-color: #ffffff;
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    flex: 1 1 100%;
    width: 100%;
    min-height: calc(100% - 3rem); /* subtracted footer height */
    /* width: ${({ $isSidebarOpen }) => ($isSidebarOpen ? 'calc(100% - 15.384rem) !important' : '100%')}; */
    padding-left: ${({ $isSidebarOpen }) => ($isSidebarOpen ? '15.75rem' : '3.875rem')};
    transition: padding-left 0.5s ease;
`;

export const StyledTablePage = styled.div.attrs({
    className: 'StyledTablePage',
})`
    h3 {
        font-family: ${fonts.poppins};
        font-weight: 500;
        font-size: 16px;
        color: var(--steel-950);
        margin: 0;
    }

    .title-container {
        position: sticky;
        top: 0;
        z-index: 1000;
        background-color: #f9fafb;
        display: flex;
        padding: 24px;
        align-items: center;
        height: 66px;
        justify-content: space-between;
        margin-bottom: 1.5rem;

        div {
            display: flex;
            align-items: center;
            gap: 1rem;
        }
    }
`;

// Typed wrapper for lazyLoader to ensure it always returns a Promise
export const typedLazyLoader = (importFn: () => Promise<{ default: ComponentType<any> }>): Promise<{ default: ComponentType<any> }> => {
    const result = lazyLoader(importFn);
    // If lazyLoader returns undefined (cache buster case), return a promise that never resolves
    // (page will reload anyway, so this is safe)
    if (!result) {
        return new Promise(() => {});
    }
    return result as Promise<{ default: ComponentType<any> }>;
};

// Routes
const Error404 = lazy(() => typedLazyLoader(() => import('components/Error404')));
const Processes = lazy(() => typedLazyLoader(() => import('app/routes/Processes')));
const Users = lazy(() => typedLazyLoader(() => import('app/routes/Users')));
const Opportunities = lazy(() => typedLazyLoader(() => import('app/routes/Opportunities')));
const Product = lazy(() => typedLazyLoader(() => import('app/routes/Products')));
const User = lazy(() => typedLazyLoader(() => import('app/routes/User')));
const CookiePolicy = lazy(() => typedLazyLoader(() => import('app/routes/CookiePolicy')));
const CreateUser = lazy(() => typedLazyLoader(() => import('app/routes/Users/create')));
const EditUser = lazy(() => typedLazyLoader(() => import('app/routes/Users/edit')));

const Restricted = () => {
    const {
        user,
        actions: { logout },
    } = useUserStore();
    const { sidebarOpen } = useSidebarStore();

    const location = useLocation();

    const prevLocation = useRef(location.pathname);

    //#region Cancel requests on page leave

    history.listen((location) => {
        //@ts-ignore
        if (prevLocation.current !== location.pathname) {
            window.stop();
            //@ts-ignore
            prevLocation.current = location.pathname;
        }
    });
    //#endregion

    const hasToken = isFieldDefined(localStorage?.getItem('userToken'));

    useEffect(() => {
        // Must be in a use effect to avoid updating store state while rendering this component. Use effect only runs after renders.
        if (!hasToken && user.auth.isUserAuthenticated) {
            //istokenvalido
            toast.warning(SESSION_EXPIRED);
            logout();
        }
    }, [user.auth.isUserAuthenticated, hasToken]); // eslint-disable-line

    if (!(user.auth.isUserAuthenticated && hasToken)) {
        return <Navigate to={{ pathname: BASENAME_URL_LOGIN }} />;
    }

    return (
        <div className={`app-container`}>
            {user.auth.isUserAuthenticated && (
                <div className="app-main-container">
                    {/* <Header /> */}
                    <Sidebar />
                    <main id="main-container" className="app-main-content-wrapper">
                        {/* <context> */}
                        <StyledAppMainContent $isSidebarOpen={sidebarOpen}>
                            <Suspense fallback={<Loading useTopLoading={true} size={30} />}>
                                <Routes>
                                    <Route path={`processes`} element={<Processes />} />
                                    <Route path={`users`} element={<Users />} />
                                    <Route path={`users/create`} element={<CreateUser />} />
                                    <Route path={`users/edit/:id`} element={<EditUser />} />
                                    <Route path={`opportunities/:id`} element={<Product />} />
                                    <Route path={`opportunities/*`} element={<Opportunities />} />
                                    <Route path={`user`} element={<User />} />
                                    <Route path={`cookie-policies`} element={<CookiePolicy />} />
                                    <Route path="*" element={<Error404 />} />
                                </Routes>
                            </Suspense>
                        </StyledAppMainContent>
                        {/* <Footer isUserAuthenticated={user.auth.isUserAuthenticated} /> */}
                        {!user.changed_pass && <RedefinePasswordDialog />}
                    </main>
                </div>
            )}
        </div>
    );
};

export default Restricted;
